/**
 * The seller storefront's public URL.
 *
 * WHY A HELPER AND NOT 16 TEMPLATE LITERALS. Before this module every caller
 * built the path inline, and they had already drifted: browse/page.tsx and the
 * old server-rendered product body wrapped the username in encodeURIComponent, cart/page.tsx
 * and product-card.tsx did not. One of those is wrong for any username the
 * validator ever loosens. Routing all of them through one function is also what
 * lets storefront-url.test.ts assert the mapping once instead of per call site.
 *
 * WHY THE PHYSICAL ROUTE IS STILL /profile/[username]. The App Router reserves a
 * leading `@` on a directory name for a PARALLEL ROUTE SLOT
 * (isParallelRouteSegment in next/dist/shared/lib/segment.js), and
 * normalizeAppPath drops such a segment from the route path entirely. So
 * src/app/@[username]/ would register a slot hanging off "/" and would never
 * serve a /@handle URL. The public path therefore comes from an afterFiles
 * rewrite in next.config.js, with a permanent redirect off the old path.
 *
 * No React, no DOM, no Firestore, so the type-stripping node test runner can
 * load it.
 */

/** The public prefix. A storefront lives at `/@{username}`. */
export const STOREFRONT_PREFIX = '/@';

/** The internal route the rewrite targets. Kept here so both halves move together. */
export const STOREFRONT_ROUTE_PREFIX = '/profile/';

/**
 * The public href for a seller's storefront.
 *
 * Encoded, because a username is user-supplied. The validator restricts it to
 * [A-Za-z0-9_] today, for which encodeURIComponent is a no-op, but a legacy row
 * predating that rule must still produce a path that resolves rather than one
 * that silently breaks the link.
 *
 * ACCEPTS null AND undefined DELIBERATELY. Several call sites link to the other
 * party in a conversation, a dispute or a sale, and that username is nullable
 * because the account may have been deleted. Every one of them previously built
 * the path with a template literal, which stringifies the absent value and
 * shipped a link to "/profile/undefined". Collapsing to the bare prefix is not
 * a good link either, but it is an obviously dead one rather than a plausible
 * looking URL, and it keeps the nullability visible at the call site instead of
 * forcing a cast. Callers that can render nothing at all should still do that.
 */
export function storefrontHref(username: string | null | undefined): string {
  return `${STOREFRONT_PREFIX}${encodeURIComponent(username ?? '')}`;
}

/**
 * Spread onto every link to a storefront:
 * `<Link href={storefrontHref(name)} {...STOREFRONT_LINK_PROPS}>`.
 *
 * A storefront opens in a NEW TAB and the page it was clicked from stays as it
 * was (owner decision 2026-10-04), so the marketplace, a product page, the cart
 * or a dashboard table keeps its scroll position, filters and state. `noopener`
 * because the new tab has no reason to reach back into this one through
 * window.opener. Code that navigates instead of rendering a link uses
 * `window.open(href, '_blank', 'noopener')`, never router.push.
 * storefront-url.test.ts fails on a storefront link without these props. The
 * one same-tab link is the storefront nav's link to its own page.
 */
export const STOREFRONT_LINK_PROPS = { target: '_blank', rel: 'noopener' } as const;

/**
 * Whether a pathname is a public storefront.
 *
 * MATCHES BOTH FORMS ON PURPOSE. `/@alice` is what the browser shows and what a
 * client navigation reports; `/profile/alice` is the internal route the rewrite
 * resolves to, and it is what a server component sees. Callers that gate chrome
 * on this run in both places, and a check that recognised only one form would
 * put the global header back on half the requests. The old path also still
 * arrives directly until every external link has followed the 301.
 *
 * `/dashboard/profile` is a different path and does not match: the prefix test
 * is anchored, and a storefront segment must be non-empty.
 */
export function isStorefrontPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return storefrontUsernameFromPath(pathname) !== null;
}

/**
 * The username a storefront path names, or null if it is not a storefront path.
 *
 * Returns null for a bare `/@` or `/profile/` with nothing after it, and for a
 * nested path like `/profile/alice/settings`, so this cannot claim a future
 * sub-route as a storefront.
 */
export function storefrontUsernameFromPath(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  const path = pathname.split('?')[0].split('#')[0];
  const trimmed = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  for (const prefix of [STOREFRONT_PREFIX, STOREFRONT_ROUTE_PREFIX]) {
    if (!trimmed.startsWith(prefix)) continue;
    const rest = trimmed.slice(prefix.length);
    if (!rest || rest.includes('/')) continue;
    try {
      return decodeURIComponent(rest);
    } catch {
      // A malformed percent sequence is not a username we can resolve, but it
      // is still a storefront-shaped path, so hand back the raw segment rather
      // than throwing out of a function callers use to pick a layout.
      return rest;
    }
  }
  return null;
}
